import type { ChartSpec } from "./types";

// xlsx is ~400KB minified and only admins parse Excel, so it is loaded on
// demand (and cached) instead of riding in the main bundle every member loads.
type XlsxModule = typeof import("xlsx");
let xlsxModule: Promise<XlsxModule> | undefined;
function loadXlsx(): Promise<XlsxModule> {
  xlsxModule ??= import("xlsx");
  return xlsxModule;
}

export interface CompanySeries {
  name: string;
  labels: string[];
  values: number[];
}

function toNumber(value: unknown): number {
  // Strip thousands separators and a trailing percent sign ("30.24%" -> 30.24),
  // since some sheets store metrics as formatted percent strings.
  const n = Number(String(value ?? "").replace(/[,%]/g, "").trim());
  return Number.isFinite(n) ? n : NaN;
}

export interface WorkbookTable {
  /** Selectable data column names (everything after the first/time column). */
  columns: string[];
  /** First-column values = the shared time/category axis. */
  labels: string[];
  /** Numeric values for each data column, keyed by column name. */
  valuesByColumn: Record<string, number[]>;
}

/**
 * Reads a workbook whose FIRST column is the time/category axis and whose
 * remaining columns are selectable data series (e.g. a stock's daily quote:
 * date | open | close | high | ...). The caller chooses which column to chart.
 */
export async function readWorkbookTable(data: ArrayBuffer): Promise<WorkbookTable> {
  const XLSX = await loadXlsx();
  const workbook = XLSX.read(new Uint8Array(data), { type: "array", cellDates: true });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return { columns: [], labels: [], valuesByColumn: {} };
  const rows = XLSX.utils
    .sheet_to_json<Array<string | number | Date>>(sheet, { header: 1, raw: false })
    .filter((row) => row.length > 0 && row.some((cell) => cell != null && String(cell).trim() !== ""));
  if (rows.length < 2) return { columns: [], labels: [], valuesByColumn: {} };

  const header = rows[0];
  const headerLength = header.length;

  // Keep only real data rows: at least one metric column must hold an actual
  // number (empty cells don't count). This drops footer/source rows like
  // "数据来源：妙想Choice" from the time axis.
  let dataRows = rows.slice(1).filter((row) => {
    for (let col = 1; col < headerLength; col++) {
      const cell = String(row[col] ?? "").replace(/,/g, "").trim();
      if (cell !== "" && Number.isFinite(Number(cell))) return true;
    }
    return false;
  });

  // Sort chronologically (oldest -> newest) when the first column is date-like,
  // so a file stored newest-first still charts left=old, right=new and the
  // start/end pickers read naturally.
  if (dataRows.length > 1 && dataRows.every((row) => !Number.isNaN(Date.parse(String(row[0] ?? ""))))) {
    dataRows = [...dataRows].sort((a, b) => Date.parse(String(a[0])) - Date.parse(String(b[0])));
  }

  const labels = dataRows.map((row) => String(row[0] ?? ""));
  const columns: string[] = [];
  const valuesByColumn: Record<string, number[]> = {};
  for (let col = 1; col < header.length; col++) {
    const rawName = String(header[col] ?? "").trim();
    const name = rawName || `col${col}`;
    if (valuesByColumn[name]) continue; // skip duplicate header names
    columns.push(name);
    valuesByColumn[name] = dataRows.map((row) => {
      const n = toNumber(row[col]);
      return Number.isNaN(n) ? 0 : n;
    });
  }
  return { columns, labels, valuesByColumn };
}

export interface ScatterTable {
  /** Category (类别) per data row. */
  categories: string[];
  /** Industry name (行业) per data row. */
  names: string[];
  /** Selectable numeric column header names, in sheet order. */
  numericColumns: string[];
  /** Numeric values for each numeric column, aligned to `names`/`categories`. */
  valuesByColumn: Record<string, number[]>;
}

// Parses an industry "scatter" sheet (one row per industry) generically: finds
// the header row, the 类别/行业 columns, and every NUMERIC data column. The UI
// then lets the admin pick which numeric columns are the X and Y axes — so the
// same tool serves momentum (涨跌幅 vs 涨跌幅), crowding (相对位置 vs 变化), and
// any future two-metric industry scatter. Date/code columns are excluded.
export async function parseScatterRows(data: ArrayBuffer): Promise<ScatterTable> {
  const XLSX = await loadXlsx();
  const workbook = XLSX.read(new Uint8Array(data), { type: "array", cellDates: true });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return { categories: [], names: [], numericColumns: [], valuesByColumn: {} };
  const grid = XLSX.utils.sheet_to_json<Array<string | number>>(sheet, { header: 1, raw: false });

  // Header row = the first row that names an 行业/名称 column.
  let headerRow = 0;
  for (let r = 0; r < Math.min(12, grid.length); r += 1) {
    const cells = (grid[r] ?? []).map((c) => String(c ?? ""));
    if (cells.some((c) => c.includes("行业") || c.includes("名称"))) {
      headerRow = r;
      break;
    }
  }
  const header = (grid[headerRow] ?? []).map((c) => String(c ?? "").trim());
  const catCol = header.findIndex((h) => h.includes("类别") || h.includes("分类") || h === "类");
  const nameCol = header.findIndex((h) => h.includes("行业") || h.includes("名称"));

  const dataRows = grid.slice(headerRow + 1).filter((row) => String(row?.[nameCol] ?? "").trim() !== "");
  const categories = dataRows.map((row) => String(row[catCol] ?? "").trim() || "기타");
  const names = dataRows.map((row) => String(row[nameCol] ?? "").trim());

  const numericColumns: string[] = [];
  const valuesByColumn: Record<string, number[]> = {};
  for (let c = 0; c < header.length; c += 1) {
    if (c === catCol || c === nameCol) continue;
    const name = header[c];
    if (!name || valuesByColumn[name]) continue;
    if (/日期|代码|date|^\d{4}[-/.]\d/i.test(name)) continue; // skip date/code columns
    const values = dataRows.map((row) => toNumber(row[c]));
    const finite = values.filter((v) => Number.isFinite(v)).length;
    if (finite >= Math.max(1, Math.floor(dataRows.length * 0.5))) {
      numericColumns.push(name);
      valuesByColumn[name] = values;
    }
  }
  return { categories, names, numericColumns, valuesByColumn };
}

// Picks sensible default X/Y columns: a pair of 涨跌幅 columns, else
// 相对位置 / 相对位置变化, else the first two numeric columns.
export function defaultScatterAxes(columns: string[]): { x: string; y: string } {
  const change = columns.filter((c) => c.includes("涨跌幅"));
  if (change.length >= 2) return { x: change[0], y: change[1] };
  const relPos =
    columns.find((c) => c === "相对位置") ??
    columns.find((c) => c.includes("相对位置") && !c.includes("前一") && !c.includes("变化"));
  const relChange = columns.find((c) => c.includes("相对位置变化")) ?? columns.find((c) => c.includes("变化"));
  if (relPos && relChange) return { x: relPos, y: relChange };
  return { x: columns[0] ?? "", y: columns[1] ?? columns[0] ?? "" };
}

/** Convenience: just the selectable column names of a workbook. */
/**
 * Resolves a column's values across same-format files. Matches by header name
 * first; if a file's header differs (e.g. it embeds a per-file number like
 * "1,332,867.23x/..."), falls back to the same column INDEX.
 */
export function columnValues(table: WorkbookTable, name: string, index: number): number[] | undefined {
  const byName = table.valuesByColumn[name];
  if (byName) return byName;
  const positional = index >= 0 ? table.columns[index] : undefined;
  return positional ? table.valuesByColumn[positional] : undefined;
}

/**
 * Merges multiple single-company series into one overlay chart, aligning on the
 * union of period labels (missing periods become 0). Rendered as a line chart.
 */
export function buildComparisonChart(companies: CompanySeries[], title: string, unit?: string): ChartSpec {
  const labels: string[] = [];
  for (const company of companies) {
    for (const label of company.labels) {
      if (!labels.includes(label)) labels.push(label);
    }
  }
  const series = companies.map((company) => {
    const byLabel = new Map(company.labels.map((label, index) => [label, company.values[index] ?? 0]));
    return { name: company.name, values: labels.map((label) => byLabel.get(label) ?? 0) };
  });
  return {
    id: `comparison_${Date.now()}`,
    title,
    unit,
    labels,
    series,
    kind: "line"
  };
}

export function chartsToCsv(charts: ChartSpec[]): string {
  const lines = ["chart,series,label,value"];
  for (const chart of charts) {
    for (const series of chart.series) {
      chart.labels.forEach((label, index) => {
        lines.push([chart.title, series.name, label, series.values[index] ?? 0].map(escapeCsv).join(","));
      });
    }
  }
  return lines.join("\n");
}

function escapeCsv(value: string | number): string {
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * Percentile rank (0–100) of the LATEST value within its own history:
 * 0 = lowest in history, 100 = highest. Used by the quantile-forecast tool.
 */
export function latestPercentile(values: number[]): number {
  if (!values.length) return 0;
  const current = values[values.length - 1];
  if (values.length === 1) return 100;
  return (values.filter((v) => v < current).length / (values.length - 1)) * 100;
}
